import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard,
  Inventory,
  Inventory2,
  Category,
  ShoppingCart,
  People,
  Assessment,
  Settings,
  Menu,
  Close,
} from '@mui/icons-material';

const Sidebar = React.memo(({ currentPage, setCurrentPage }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isOpen = useSelector((state) => state.ui.sidebarOpen);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleToggle = useCallback(() => {
    dispatch(toggleSidebar());
  }, [dispatch]);
  
  const handleClose = useCallback(() => {
    dispatch(toggleSidebar());
  }, [dispatch]);
  const menuItems = useMemo(() => [
    { text: 'Dashboard', icon: <Dashboard />, page: 'dashboard', active: currentPage === 'dashboard' },
    { text: 'Products', icon: <Inventory />, page: 'products', active: currentPage === 'products' },
    { text: 'Categories', icon: <Category />, page: 'categories', active: currentPage === 'categories' },
    { text: 'Inventory', icon: <Inventory2 />, page: 'inventory', active: currentPage === 'inventory' },
    { text: 'Orders', icon: <ShoppingCart />, page: 'orders', active: currentPage === 'orders' },
    { text: 'Customers', icon: <People />, page: 'customers', active: currentPage === 'customers' },
    { text: 'Reports', icon: <Assessment />, page: 'reports', active: currentPage === 'reports' },
    { text: 'Settings', icon: <Settings />, page: 'settings', active: currentPage === 'settings' },
  ], [currentPage]);



  const handlePageClick = useCallback((page) => {
    setCurrentPage(page);
  }, [setCurrentPage]);

  return (
    <>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isOpen}
        onClose={handleClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: isMobile ? 280 : (isOpen ? 280 : 80),
          flexShrink: 0,
          zIndex: isMobile ? 1300 : 1200,
          '& .MuiDrawer-paper': {
            width: isMobile ? 280 : (isOpen ? 280 : 80),
            boxSizing: 'border-box',
            background: theme.palette.mode === 'dark'
              ? 'rgba(15, 15, 35, 0.85)'
              : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            border: 'none',
            borderRight: isMobile ? 'none' : `1px solid ${theme.palette.divider}30`,
            transition: 'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            boxShadow: isMobile 
              ? `12px 0 40px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)'}`
              : `6px 0 30px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)'}`,
            overflow: 'hidden',
          },
        }}
      >
        {/* Logo Section */}
        <Box sx={{ p: 2, pb: 1 }}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: (isOpen || isMobile) ? 'flex-start' : 'center',
              mb: 1,
            }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  background: '#000000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: '#1a1a1a',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/logo.svg"
                  alt="EcomAdmin Pro"
                  sx={{
                    width: 32,
                    height: 32,
                    position: 'relative',
                    zIndex: 1,
                    filter: 'none',
                  }}
                />
              </Box>
              <AnimatePresence>
                {(isOpen || isMobile) && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                    style={{ marginLeft: 16 }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          lineHeight: 1.2,
                          mb: 0.2,
                        }}
                      >
                        EcomAdmin Pro
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          opacity: 0.8,
                        }}
                      >
                        E-commerce Dashboard
                      </Typography>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </motion.div>
        </Box>

        {/* Toggle Button */}
        {!isMobile && (
          <Box sx={{ px: 2, display: 'flex', justifyContent: 'center', mb: 2 }}>
            <motion.div 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
            >
              <IconButton 
                onClick={handleToggle} 
                size="small"
                sx={{ 
                  color: theme.palette.text.secondary,
                  background: `${theme.palette.background.paper}80`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${theme.palette.divider}`,
                  width: 36,
                  height: 36,
                  '&:hover': {
                    background: `${theme.palette.primary.main}15`,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Menu sx={{ fontSize: 18 }} />
                </motion.div>
              </IconButton>
            </motion.div>
          </Box>
        )}
        
        {/* Mobile Close Button */}
        {isMobile && (
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <IconButton 
              onClick={handleClose}
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        )}

        <List sx={{ px: 1.5, mt: 1, flex: 1 }}>
          {menuItems.map((item, index) => (
            <motion.div
              key={item.text}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              whileHover={{ 
                scale: 1.02, 
                x: (isOpen || isMobile) ? 6 : 0,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <ListItem
                button
                onClick={() => {
                  handlePageClick(item.page);
                  if (isMobile) {
                    handleClose();
                  }
                }}
                sx={{
                  mb: 0.5,
                  borderRadius: 2.5,
                  mx: 0.5,
                  minHeight: 48,
                  background: item.active
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(99, 102, 241, 0.2)'
                      : 'rgba(99, 102, 241, 0.15)'
                    : 'transparent',
                  backdropFilter: item.active ? 'blur(20px)' : 'none',
                  border: item.active 
                    ? `1px solid ${theme.palette.primary.main}40` 
                    : '1px solid transparent',
                  '&:hover': {
                    background: item.active
                      ? theme.palette.mode === 'dark'
                        ? 'rgba(99, 102, 241, 0.25)'
                        : 'rgba(99, 102, 241, 0.2)'
                      : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${theme.palette.primary.main}30`,
                    boxShadow: `0 4px 20px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                  },
                  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  cursor: 'pointer',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: item.active 
                      ? theme.palette.primary.main 
                      : theme.palette.text.secondary,
                    minWidth: 40,
                    transition: 'color 0.2s ease',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <AnimatePresence>
                  {(isOpen || isMobile) && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                      <ListItemText
                        primary={item.text}
                        sx={{
                          '& .MuiTypography-root': {
                            color: item.active 
                              ? theme.palette.primary.main 
                              : theme.palette.text.primary,
                            fontWeight: item.active ? 600 : 500,
                            fontSize: '0.9rem',
                            transition: 'color 0.2s ease',
                          },
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </ListItem>
            </motion.div>
          ))}
        </List>
      </Drawer>
    </>
  );

});

export default Sidebar;